import { Autocomplete, Box, TextField } from "@mui/material";
import { styled } from "@mui/system";
import { useState } from "react";
import ReactCountryFlag from "react-country-flag";

import { CountryRegion, COUNTRIES, REGIONS } from "~src/data/countries";

type countryRegionProps = {
  onSelect: (value: CountryRegion[]) => void;
  localCountryValues: CountryRegion[];
  setLocalCountryValues: React.Dispatch<React.SetStateAction<CountryRegion[]>>;
};

const GroupHeader = styled("div")(() => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  fontFamily: "Poppins",
  fontWeight: "bolder",
  color: "#6E788A",
  backgroundColor: "rgba(59, 78, 223, 0.1)",
}));

const GroupItems = styled("ul")({
  padding: 0,
  fontFamily: "Poppins !important",
});

export default function CountryRegions({
  onSelect,
  localCountryValues,
  setLocalCountryValues,
}: countryRegionProps) {
  const [options] = useState<CountryRegion[]>(getCountryRegionOptions());

  return (
    <Autocomplete
      multiple
      options={options}
      value={localCountryValues}
      onChange={(_, value) => {
        setLocalCountryValues(value);
        onSelect(value);
      }}
      autoHighlight
      groupBy={(option) => option.groupBy}
      getOptionLabel={(option) => option.label}
      renderOption={(props, option) => {
        // Use a type assertion to access the key property
        const propsWithKey = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
        const { key, ...otherProps } = propsWithKey;
        
        return (
          <Box
            sx={{
              "& span": {
                marginRight: 1.5,
                fontSize: 18,
              },
            }}
            key={key}
            {...otherProps}
            component={"li"}
          >
            {option.groupBy === GroupBy.country && (
              <span>
                <ReactCountryFlag countryCode={option.code} svg />
              </span>
            )}
            {option.label}
          </Box>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label="Country/Region"
            slotProps={{
              htmlInput: {
                ...params.inputProps,
              }
            }}
          />
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader>
            <strong>{params.group}</strong>
          </GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
    />
  );
}

enum GroupBy {
  region = "REGION",
  country = "COUNTRY",
}

const getCountryRegionOptions = () => {
  return [
    ...REGIONS.map((region) => ({
      code: region,
      label: region,
      groupBy: GroupBy.region,
    })),
    ...COUNTRIES.map((country) => ({
      code: country.code,
      label: country.label,
      groupBy: GroupBy.country,
    })),
  ] as CountryRegion[];
};
